/**
 * HANNE BLOCH, a complimentary mini bag with orders over an amount (2026-10-09, gift wireframes v1 to v4, Vic's final
 * notes of 2026-10-09). Loaded by snippets/hb-tgift.liquid for consumers only, when the feature is on.
 *
 * The markup is Liquid: the module in the cart (snippets/hb-cart-tgift, re-rendered with the drawer on every cart
 * change), the slide-up and the note (snippets/hb-tgift). The cart's figures come from snippets/hb-tgift-calc, inline
 * at page load and from sections/hb-tgift-state after every cart change. This script:
 *   - the module: tap a swatch to choose; RESERVE MY GIFT (cart attribute hb_gift_reserved = the bag's handle);
 *     reserved, one tap on another swatch changes the reservation at once (Vic: option B) and the line reads
 *     "Changed your gift." for 2.5 s, crossfading back to "We add it to your cart at DKK 2,000"; over the amount
 *     ADD MY GIFT; the picture opens the slide-up;
 *   - at the amount: the reserved bag goes into the cart by itself (properties _free_gift = Yes, _hb_gift = threshold).
 *     In an open cart her piece arrives first (0.7 s), the module folds away (0.35 s) and the bag floats up into the
 *     list with "Added to your cart" for 3 s; on a collection page the note says "Your gift is added to your cart";
 *   - under the amount again: the bag leaves, the reservation stays. Removing the bag herself ends the reservation;
 *     Undo brings both back;
 *   - the slide-up: RESERVE MY GIFT, RESERVED with a check, then after 2 s a slow crossfade to CONTINUE SHOPPING;
 *     over the amount ADD GIFT TO MY ORDER (or "In your cart"; with another bag in the cart, CHANGE MY GIFT, 2026-10-10);
 *   - the bag's line in the drawer: "Color: …" opens the bags' swatches in the line's band (snippets/hb-cart-lines,
 *     assets/hb-cart.js), a tap swaps the bag in place; the reservation follows the bag through decide() below;
 *   - the note (collection pages): rises 0.9 s after the page, stays 5 s (longer while the mouse or focus is on it),
 *     once per visit for each kind, never over the cart, a quick add, the gift pop-up or the quick add's own note,
 *     and never once a gift is reserved. A note due while the cart is open (her accepted set took the cart over the
 *     amount) comes once the cart closes, 5 s after it (HB 2026-10-10, Vic: "the 'we have a gift for you' tile should appear"; "give it 5s or so").
 *   - the cart's own changes (assets/hb-cart.js) carry this feature's state section with them, so the module's mode
 *     is right the moment the drawer re-renders (HB 2026-10-10); other changes are fetched as before.
 * Safety: with the feature on for customers (not a ?hb_gift=preview cart), a bag that does not come in at 0 (the
 * automatic discount is missing or does not apply) is taken straight back out and the feature rests for the visit.
 * Every cart request asks for the drawer's section and this feature's state, and hands the drawer to the theme through
 * one CartUpdateEvent (source hb-tgift) without opening a closed cart.
 */
import { CartAddEvent, CartUpdateEvent } from '@theme/events';

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const SECTION = 'hb-tgift-state';
const SETTLE_MS = 700; // in an open cart her piece arrives first, then the bag
const FOLD_MS = 350; // the module folds away
const ARRIVE_MS = 600; // the bag floats up into the list
const ADDED_MS = 3000; // "Added to your cart" on the bag's line
const RESERVED_MS = 2000; // RESERVED, then CONTINUE SHOPPING
const CHANGED_MS = 2500; // "Changed your gift."
const SHEET_MS = 550;
const NOTE_DELAY_MS = 900;
const NOTE_VISIT_MS = 20000; // HB 2026-10-10 (Vic): the first note comes 20 s into her visit, across collection pages (12 s on 9 Oct)
const NOTE_MS = 5000;
const NOTE_AFTER_CART_MS = 5000; // HB 2026-10-10 (Vic): no note within 5 s of the cart closing (0.9 s "too stressful")
const SEEN_KEY = 'hb-tg-note-seen';
const FIRST_KEY = 'hb-tg-first'; // when her first collection page of this visit opened
const OFF_KEY = 'hb-tg-off';
const EVENT = CartAddEvent.eventName || 'cart:update';
const CHECK_SVG =
  '<svg width="11" height="11" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M4.75 10.75l2.93 3.84c.1.14.16.21.22.23a.2.2 0 0 0 .18 0c.07-.02.12-.1.23-.23l6.93-8.85" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';

let DATA = {};
try {
  DATA = JSON.parse(document.getElementById('hb-tg-data')?.textContent || '{}');
} catch (e) {
  DATA = {};
}
const BAGS = Array.isArray(DATA.bags) ? DATA.bags : [];
const byVid = new Map(BAGS.map((b) => [String(b.vid), b]));
const byHandle = new Map(BAGS.map((b) => [b.handle, b]));
const firstAvail = BAGS.find((b) => b.avail);

function parse(line) {
  const p = String(line || '').trim().split('|');
  return {
    on: p[0] === 'true',
    thr: Number(p[1]) || 0,
    sub: Number(p[2]) || 0,
    ship: Number(p[3]) || 0,
    tot: Number(p[4]) || 0,
    res: p[5] || '',
    resVid: p[6] || '',
    bagKey: p[7] || '',
    bagVid: p[8] || '',
    bagHandle: p[9] || '',
    bagFinal: Number(p[10]) || 0,
    other: p[11] === 'true',
    preview: p[12] === 'true',
    live: p[13] === 'true',
  };
}

function recall(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
}
function remember(key, value) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (e) {
    /* no storage: the note may show again on the next page */
  }
}

const S = {
  cur: parse(DATA.state),
  off: recall(OFF_KEY) === '1',
  busy: false,
  redecide: false,
  refetch: false,
  sel: null, // a bag she picked before reserving (module and slide-up share it)
  flash: false, // "Changed your gift."
  flashTimer: 0,
  phase: '', // the slide-up's button after RESERVE MY GIFT: 'reserved', then 'continue'
  phaseTimer: 0,
  ownRemoval: false,
  pendingArrive: null, // the bag's key: it floats in once the cart shows it
  hadMod: false,
  refreshTimer: 0,
  noteKind: null,
  noteTimer: 0,
  noteHeld: false,
  noteWait: 0,
  notePending: null, // { kind, vid }: a note that was due while the cart was open; it comes once the cart closes
  cartClosedAt: 0, // when the cart last closed: a note keeps NOTE_AFTER_CART_MS of distance from it
  stale: false, // a cart change from elsewhere, its state not fetched yet: the drawer's own markup is the fresher word
  fails: 0, // cart requests that failed in a row: after three the feature waits for her next change
};
if (S.off) document.documentElement.classList.add('hb-tg-off');

const over = (st) => st.thr > 0 && st.sub >= st.thr;
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const drawer = () => document.querySelector('cart-drawer-component');
const dialogEl = () => document.querySelector('cart-drawer-component dialog');
const drawerOpen = () => !!(dialogEl() && dialogEl().open);
const sectionId = () => document.querySelector('cart-drawer-component cart-items-component')?.dataset.sectionId;
const mod = () => document.getElementById('hb-tg-mod');
const dlg = document.getElementById('hb-tg-dlg');
const noteEl = document.querySelector('[data-hb-tg-note]');

function modeOf(st) {
  if (st.resVid) return 'reserved';
  return over(st) ? 'add' : 'reserve';
}

// The bag on show: her reservation, else what she picked, else the note's bag (BW Knit), else the first in stock.
function currentSel() {
  const st = S.cur;
  if (st.resVid && !over(st)) return String(st.resVid);
  if (S.sel && byVid.get(S.sel)?.avail) return S.sel;
  if (st.resVid) return String(st.resVid);
  if (DATA.feature && byVid.get(String(DATA.feature))?.avail) return String(DATA.feature);
  return firstAvail ? String(firstAvail.vid) : '';
}

/* ---------- cart requests ---------- */

async function post(path, body) {
  const sid = sectionId();
  const sections = sid ? [sid, SECTION] : [SECTION];
  const res = await fetch(`${ROOT}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(Object.assign({}, body, { sections: sections.join(','), sections_url: window.location.pathname })),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.status) {
    const err = new Error(data.description || data.message || `${path} failed`);
    err.data = data;
    throw err;
  }
  return data;
}

function stateFrom(data) {
  const html = data && data.sections && data.sections[SECTION];
  if (!html) return null;
  const el = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-hb-tg-state]');
  if (!el) return null;
  try {
    return parse(JSON.parse(el.textContent));
  } catch (e) {
    return null;
  }
}

async function fetchState() {
  const res = await fetch(`${ROOT}?sections=${SECTION}`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
  if (!res.ok) return null;
  const sections = await res.json().catch(() => null);
  return sections ? stateFrom({ sections }) : null;
}

function countFrom(data) {
  const html = data && data.sections && data.sections[sectionId()];
  if (!html) return undefined;
  const n = new DOMParser().parseFromString(html, 'text/html').querySelector('[ref="cartItemCount"]')?.textContent;
  return n != null && n.trim() !== '' ? parseInt(n, 10) || 0 : 0;
}

// Vic's rule from the cart (assets/hb-cart.js): while the cart is open, changes made inside it never change its height.
function holdOpenCart(dlgEl) {
  const hb = window.HBCart && window.HBCart.state;
  if (hb && !hb.keepMode) hb.keepMode = dlgEl.classList.contains('hb-cart--full') ? 'full' : 'sheet';
  const root = document.documentElement;
  if (!window.matchMedia('(max-width: 749px)').matches || root.classList.contains('hb-cart-held')) return;
  if (!dlgEl.classList.contains('hb-cart--sheet')) return;
  root.style.setProperty('--hb-cart-h', `${Math.round(dlgEl.getBoundingClientRect().height)}px`);
  root.classList.add('hb-cart-held');
}

// Hands the new drawer to the theme. A closed cart stays closed (its auto-open is lifted for this one event, as the
// quick add does in assets/hb-cart.js).
function publish(data) {
  const sid = sectionId();
  const html = sid && data && data.sections && data.sections[sid];
  if (!html) return;
  const root = drawer();
  const dlgEl = dialogEl();
  const open = !!(dlgEl && dlgEl.open);
  if (open) holdOpenCart(dlgEl);
  const lift = !open && root && root.hasAttribute('auto-open');
  const value = lift ? root.getAttribute('auto-open') : null;
  if (lift) root.removeAttribute('auto-open');
  try {
    document.dispatchEvent(
      new CartUpdateEvent({}, 'hb-tgift', { source: 'hb-tgift', itemCount: countFrom(data), sections: { [sid]: html } })
    );
  } finally {
    if (lift) setTimeout(() => root.setAttribute('auto-open', value || ''), 0);
  }
}

// One cart action at a time; whatever came in meanwhile is looked at once it is done.
async function run(job) {
  S.busy = true;
  try {
    const out = await job();
    S.fails = 0;
    return out;
  } catch (err) {
    console.warn('[HB gift]', err);
    S.fails += 1;
    S.refetch = true;
    return null;
  } finally {
    S.busy = false;
    if (S.refetch) {
      S.refetch = false;
      S.redecide = false;
      scheduleRefresh(60);
    } else if (S.redecide) {
      S.redecide = false;
      queueMicrotask(decide);
    }
  }
}

function scheduleRefresh(ms = 150) {
  clearTimeout(S.refreshTimer);
  S.refreshTimer = setTimeout(refresh, ms);
}

async function refresh() {
  if (S.busy) {
    S.refetch = true;
    return;
  }
  try {
    const st = await fetchState();
    if (st) apply(st);
  } catch (e) {
    /* the next cart change tries again */
  }
}

/* ---------- what follows from the cart ---------- */

let prev = null;
function apply(st) {
  if (!st) return;
  prev = S.cur;
  S.cur = st;
  S.stale = false;
  paintAll();
  decide();
}

function decide() {
  const st = S.cur;
  if (!st.on || S.off || S.fails >= 3) return;
  if (S.busy) {
    S.redecide = true;
    return;
  }
  const before = prev;
  prev = st;
  // She took the bag out herself (Remove in the cart): the reservation goes with it.
  if (before && before.bagKey && !st.bagKey && !S.ownRemoval && over(st) && st.res && st.res === before.bagHandle) {
    setReservation('');
    return;
  }
  // A bag of ours in the cart that is not the reserved one (Undo brought it back): it is hers again.
  if (st.bagKey && st.res !== st.bagHandle && byHandle.has(st.bagHandle)) {
    setReservation(st.bagHandle);
    return;
  }
  // Under the amount again: the bag leaves, the reservation stays.
  if (st.bagKey && !over(st)) {
    removeBag();
    return;
  }
  // At the amount with a bag reserved: it goes in by itself.
  if (!st.bagKey && st.resVid && over(st) && !st.other) addBag(st.resVid, { auto: true });
}

/* ---------- actions ---------- */

function setReservation(handle, opts = {}) {
  const bag = handle ? byHandle.get(handle) : null;
  // Shown at once; the cart catches up.
  S.cur = Object.assign({}, S.cur, { res: handle, resVid: bag ? String(bag.vid) : '' });
  if (!handle) S.sel = null;
  if (opts.flash) startFlash();
  paintAll();
  return run(async () => {
    const data = await post('cart/update.js', { attributes: { hb_gift_reserved: handle || '' } });
    publish(data);
    const st = stateFrom(data);
    if (st) {
      prev = S.cur;
      S.cur = st;
      paintAll();
      S.redecide = true;
    }
  });
}

function startFlash() {
  S.flash = true;
  clearTimeout(S.flashTimer);
  S.flashTimer = setTimeout(() => {
    S.flash = false;
    paintAll();
  }, CHANGED_MS);
}

function addBag(vid, opts = {}) {
  const bag = byVid.get(String(vid));
  if (!bag || !bag.avail) return null;
  return run(async () => {
    if (opts.auto && drawerOpen()) await wait(SETTLE_MS);
    const m = mod();
    const folding = !!(m && drawerOpen());
    if (folding) m.classList.add('is-leaving');
    try {
      if (S.cur.res !== bag.handle) await post('cart/update.js', { attributes: { hb_gift_reserved: bag.handle } });
      // One bag of ours per order: a different one already in the cart is swapped.
      if (S.cur.bagKey && String(S.cur.bagVid) !== String(bag.vid)) {
        S.ownRemoval = true;
        await post('cart/change.js', { id: S.cur.bagKey, quantity: 0 });
      }
      const [data] = await Promise.all([
        post('cart/add.js', { items: [{ id: Number(bag.vid), quantity: 1, properties: { _free_gift: 'Yes', _hb_gift: 'threshold' } }] }),
        wait(folding ? FOLD_MS : 0),
      ]);
      const st = stateFrom(data);
      // Safety: for customers the bag must come in at 0. If it does not (no automatic discount), it goes straight back.
      if (st && st.live && !st.preview && st.bagKey && st.bagFinal > 0) {
        console.warn('[HB gift] the bag is not complimentary in this cart (is the automatic discount live?). Taken out again.');
        S.off = true;
        remember(OFF_KEY, '1');
        document.documentElement.classList.add('hb-tg-off');
        const back = await post('cart/change.js', { id: st.bagKey, quantity: 0 });
        publish(back);
        return;
      }
      const key = data && data.items && data.items[0] && data.items[0].key;
      // The bag takes the module's place, under her pieces (assets/hb-cart.js keeps the drawer's line order).
      const hb = window.HBCart && window.HBCart.state;
      if (key && hb && Array.isArray(hb.order) && !hb.order.includes(key)) hb.order.push(key);
      S.pendingArrive = key || null;
      S.sel = null;
      publish(data);
      if (st) {
        prev = st;
        S.cur = st;
        paintAll();
      }
      if (!drawerOpen() && noteEl && key) showNote('incart', bag.vid);
    } catch (err) {
      if (m) m.classList.remove('is-leaving');
      throw err;
    } finally {
      S.ownRemoval = false;
    }
  });
}

function removeBag() {
  return run(async () => {
    const key = S.cur.bagKey;
    if (!key) return;
    const line = document.querySelector(`cart-drawer-component [data-hb-line][data-key="${CSS.escape(key)}"]`);
    const going = !!(line && drawerOpen());
    if (going) {
      line.style.maxHeight = `${line.offsetHeight}px`;
      void line.offsetHeight;
      line.classList.add('hb-tg-going');
    }
    S.ownRemoval = true;
    try {
      const [data] = await Promise.all([post('cart/change.js', { id: key, quantity: 0 }), wait(going ? 350 : 0)]);
      publish(data);
      const st = stateFrom(data);
      if (st) {
        prev = st;
        S.cur = st;
        paintAll();
      }
    } finally {
      S.ownRemoval = false;
    }
  });
}

/* ---------- the module in the cart ---------- */

function paintMod() {
  const m = mod();
  const open = drawerOpen();
  if (m && !S.hadMod && open && !m.classList.contains('is-leaving')) {
    m.classList.add('is-entering');
    setTimeout(() => m.isConnected && m.classList.remove('is-entering'), 600);
  }
  S.hadMod = !!m;
  if (!m) return;
  const st = S.cur;
  const inner = m.querySelector('.hb-set__in');
  const mode = modeOf(st);
  // HB 2026-10-10: after a cart change from elsewhere the drawer's markup carries the new mode before this script has
  // the new state; it is left standing until then (it used to be painted back to the old mode for a moment).
  if (inner && !S.stale && inner.dataset.mode !== mode) inner.dataset.mode = mode;
  const sel = currentSel();
  const bag = byVid.get(sel);
  m.querySelectorAll('.hb-tg-mod__pic').forEach((p) => p.classList.toggle('is-on', p.dataset.tgVid === sel));
  m.querySelectorAll('.hb-tg-sw').forEach((b) => {
    const on = b.dataset.tgVid === sel;
    if (b.classList.contains('is-sel') !== on) b.classList.toggle('is-sel', on);
    b.setAttribute('aria-checked', on ? 'true' : 'false');
  });
  if (bag) {
    const name = m.querySelector('[data-tg-name]');
    if (name && name.textContent.replace(/\s+/g, ' ').trim() !== bag.title) name.textContent = bag.title;
    const price = m.querySelector('[data-tg-price]');
    if (price && price.textContent !== bag.price) price.textContent = bag.price;
  }
  const changed = S.flash && mode === 'reserved';
  if (m.classList.contains('is-changed') !== changed) m.classList.toggle('is-changed', changed);
}

function pick(vid) {
  const st = S.cur;
  const bag = byVid.get(String(vid));
  if (!bag || !bag.avail) return;
  // Reserved and under the amount: one tap changes the reservation (Vic, option B).
  if (st.resVid && !over(st) && String(vid) !== String(st.resVid)) {
    S.sel = String(vid);
    setReservation(bag.handle, { flash: true });
    return;
  }
  S.sel = String(vid);
  paintAll();
}

function reserveSelected() {
  const bag = byVid.get(currentSel());
  if (bag) setReservation(bag.handle);
}

function addSelected() {
  const vid = currentSel();
  if (vid) addBag(vid);
}

/* ---------- the bag's line: it floats in ---------- */

function arrive() {
  const key = S.pendingArrive;
  if (!key || !drawerOpen()) return;
  const line = document.querySelector(`cart-drawer-component [data-hb-line][data-key="${CSS.escape(key)}"]`);
  if (!line) return;
  S.pendingArrive = null;
  line.classList.add('hb-tg-arriving');
  setTimeout(() => line.isConnected && line.classList.remove('hb-tg-arriving'), ARRIVE_MS + 60);
  // HB 2026-10-10: a held sheet (assets/hb-cart.js) grows with the bag, so it never floats in below the fold of the lines.
  document.dispatchEvent(new CustomEvent('hb:tg-arrive', { detail: { key } }));
  const acts = line.querySelector('.hb-line__acts');
  if (!acts || acts.querySelector('.hb-line__added')) return;
  const note = document.createElement('span');
  note.className = 'hb-line__added';
  note.setAttribute('role', 'status');
  note.innerHTML = `${CHECK_SVG}<span>Added to your cart</span>`;
  acts.prepend(note);
  setTimeout(() => {
    note.classList.add('is-gone');
    setTimeout(() => note.remove(), 420);
  }, ADDED_MS);
}

/* ---------- the slide-up ---------- */

function sheetButton(st, sel) {
  // HB 2026-10-10 (Vic, desktop test): with a bag already in the cart, another colour on show reads CHANGE MY GIFT,
  // not ADD GIFT TO MY ORDER (it replaces hers, it is not a second gift).
  if (over(st)) {
    if (st.bagVid && String(st.bagVid) === sel) return 'incart';
    return st.bagKey ? 'change' : 'add';
  }
  if (!st.resVid || String(st.resVid) !== sel) return 'reserve';
  return S.phase === 'reserved' ? 'reserved' : 'continue';
}

function paintSheet() {
  if (!dlg) return;
  const st = S.cur;
  const sel = dlg.open && over(st) && S.sheetSel ? S.sheetSel : currentSel();
  const bag = byVid.get(sel);
  dlg.querySelectorAll('.hb-tg-stage__it').forEach((it) => it.classList.toggle('is-on', it.dataset.tgVid === sel));
  dlg.querySelectorAll('.hb-tg-sw').forEach((b) => {
    const on = b.dataset.tgVid === sel;
    if (b.classList.contains('is-sel') !== on) b.classList.toggle('is-sel', on);
    b.setAttribute('aria-checked', on ? 'true' : 'false');
  });
  if (bag) {
    const name = dlg.querySelector('[data-tg-gname]');
    if (name && name.textContent !== bag.title) name.textContent = bag.title;
    const price = dlg.querySelector('[data-tg-price]');
    if (price && price.textContent !== bag.price) price.textContent = bag.price;
  }
  const status = dlg.querySelector('[data-tg-status]');
  const show = over(st) ? 'none' : !st.resVid ? 'offer' : S.flash ? 'changed' : 'res';
  if (status && status.dataset.show !== show) status.dataset.show = show;
  const btn = dlg.querySelector('[data-tg-primary]');
  const b = sheetButton(st, sel);
  if (btn && btn.dataset.show !== b) btn.dataset.show = b;
  if (btn) btn.setAttribute('aria-disabled', b === 'incart' || b === 'reserved' ? 'true' : 'false');
}

function openSheet(vid) {
  if (!dlg) return;
  const st = S.cur;
  S.sheetSel = vid ? String(vid) : st.bagVid ? String(st.bagVid) : null;
  if (vid && !over(st) && !st.resVid) S.sel = String(vid);
  S.phase = '';
  clearTimeout(S.phaseTimer);
  dlg.querySelectorAll('img[loading="lazy"]').forEach((img) => {
    img.loading = 'eager';
  });
  paintSheet();
  hideNote();
  if (!dlg.open) {
    try {
      dlg.showModal();
    } catch (e) {
      dlg.setAttribute('open', '');
    }
  }
  document.documentElement.classList.add('hb-tg-lock');
  // From its closed place, then up (a forced style read rather than animation frames, which a background tab pauses).
  void dlg.offsetWidth;
  dlg.classList.add('is-open');
}

function closeSheet() {
  if (!dlg || !dlg.open) return;
  dlg.classList.remove('is-open');
  clearTimeout(S.phaseTimer);
  setTimeout(() => {
    if (dlg.classList.contains('is-open')) return;
    if (dlg.open) dlg.close();
    document.documentElement.classList.remove('hb-tg-lock');
    S.phase = '';
    S.sheetSel = null;
    paintSheet();
  }, SHEET_MS);
}

function sheetPick(vid) {
  const st = S.cur;
  if (over(st)) {
    S.sheetSel = String(vid);
    if (!st.bagKey) S.sel = String(vid);
    paintSheet();
    return;
  }
  pick(vid);
}

function sheetPrimary() {
  const st = S.cur;
  const sel = over(st) && S.sheetSel ? S.sheetSel : currentSel();
  const b = sheetButton(st, sel);
  const bag = byVid.get(sel);
  if (b === 'reserve' && bag) {
    S.phase = 'reserved';
    setReservation(bag.handle);
    clearTimeout(S.phaseTimer);
    S.phaseTimer = setTimeout(() => {
      S.phase = 'continue';
      paintSheet();
    }, RESERVED_MS);
  } else if (b === 'continue') {
    closeSheet();
  } else if ((b === 'add' || b === 'change') && bag) {
    // 'change': addBag swaps the bag in the cart for this one (one bag of ours per order).
    closeSheet();
    setTimeout(() => addBag(bag.vid), drawerOpen() ? 200 : 0);
  }
}

/* ---------- the note on collection pages ---------- */

function seenKinds() {
  return (recall(SEEN_KEY) || '').split(',').filter(Boolean);
}
function markSeen(kind) {
  const list = seenKinds();
  if (!list.includes(kind)) remember(SEEN_KEY, list.concat(kind).join(','));
}

function noteKindFor(st) {
  if (!st.on || st.other || S.off || st.bagKey) return null;
  // HB 2026-10-09 (Vic): every visitor on a collection page hears about the gift, an empty cart included;
  // the cart's amount gate (the module appears from the shipping amount) does not apply to the note.
  if (st.resVid) return null; // reserved: no reminder (Vic, v3)
  return over(st) ? 'waiting' : 'tease';
}

// A quick add on its way (assets/hb-cart.js marks <html> hb-qa-busy from the tap until its own note has left).
function quickAddBusy() {
  return document.documentElement.classList.contains('hb-qa-busy') || !!document.querySelector('.hb-toast.is-in');
}

function blocked() {
  return (
    drawerOpen() ||
    (dlg && dlg.open) ||
    quickAddBusy() ||
    !!document.querySelector('.variant-popup.active, #freeGiftToast.is-visible')
  );
}

function showNote(kind, vid, delay) {
  if (!noteEl) return;
  clearTimeout(S.noteWait);
  // The quick add's own note finishes first (HB 2026-10-10: from the tap, not only once its note is up); nothing
  // ever shows over the cart or a quick add.
  const tries = { n: 0 };
  const go = () => {
    if (quickAddBusy() && tries.n < 40) {
      tries.n += 1;
      S.noteWait = setTimeout(go, 300);
      return;
    }
    // HB 2026-10-10 (Vic, 14:20 CPH): she has just closed the cart; the note gives her 5 s to look around first
    // (the confirmation of her own gift, "Your gift is added to your cart", is not held back).
    const sinceClose = S.cartClosedAt ? Date.now() - S.cartClosedAt : Infinity;
    if (kind !== 'incart' && sinceClose < NOTE_AFTER_CART_MS) {
      S.noteWait = setTimeout(go, NOTE_AFTER_CART_MS - sinceClose);
      return;
    }
    if (blocked()) {
      // HB 2026-10-10 (Vic): a note due while the cart is open (the gift to be had once her accepted set took the cart
      // over the amount) waits for the cart to close and comes then, if she has not left the cart with it in hand.
      if (drawerOpen() && kind !== 'incart') S.notePending = { kind, vid };
      return;
    }
    fillNote(kind, vid);
    markSeen(kind);
    void noteEl.offsetWidth;
    noteEl.classList.add('is-in');
    armNote();
  };
  S.noteWait = setTimeout(go, typeof delay === 'number' ? delay : kind === 'incart' ? 350 : NOTE_DELAY_MS);
}

function fillNote(kind, vid) {
  const bag = byVid.get(String(vid || '')) || byVid.get(String(DATA.feature)) || firstAvail;
  const text = {
    tease: ['We have a gift for you', `With orders over ${DATA.thrMoney || ''}`, 'See the gifts'],
    // Vic, 2026-10-09 17:20: a short second line like the other two notes; it must stay on one line next to the button
    // (154 px at 375 px, BrownStd Light 11 px): this one measures 150 px, 'A complimentary mini bag' 140 px is the fallback.
    waiting: ['We have a gift for you', 'A mini bag for the essentials', 'Choose my gift'],
    incart: ['Your gift is added to your cart', bag ? bag.title : '', 'View cart'],
  }[kind];
  S.noteKind = kind;
  noteEl.querySelector('[data-tg-note-t]').textContent = text[0];
  noteEl.querySelector('[data-tg-note-s]').textContent = text[1];
  noteEl.querySelector('[data-tg-note-go]').textContent = text[2];
  const img = noteEl.querySelector('.hb-tg-note__img');
  if (img && bag && bag.img && img.getAttribute('src') !== bag.img) img.src = bag.img;
}

function armNote() {
  clearTimeout(S.noteTimer);
  if (S.noteHeld || !noteEl.classList.contains('is-in')) return;
  S.noteTimer = setTimeout(hideNote, NOTE_MS);
}

function hideNote() {
  clearTimeout(S.noteTimer);
  clearTimeout(S.noteWait);
  S.noteHeld = false;
  if (noteEl) noteEl.classList.remove('is-in');
}

/* ---------- painting and wiring ---------- */

function paintAll() {
  paintMod();
  paintSheet();
}

let scheduled = false;
function afterMorph() {
  if (scheduled) return;
  scheduled = true;
  queueMicrotask(() => {
    scheduled = false;
    paintMod();
    arrive();
  });
}

function onClick(event) {
  const t = event.target instanceof Element ? event.target : null;
  if (!t) return;
  const opener = t.closest('[data-hb-tg-open]');
  if (opener) {
    event.preventDefault();
    const line = opener.closest('[data-hb-line]');
    openSheet(line ? line.dataset.variantId : currentSel());
    return;
  }
  const sw = t.closest('.hb-tg-mod .hb-tg-sw');
  if (sw) {
    event.preventDefault();
    if (!sw.disabled) pick(sw.dataset.tgVid);
    return;
  }
  const act = t.closest('.hb-tg-mod [data-hb-tg-act]');
  if (act) {
    event.preventDefault();
    if (S.busy) return;
    if (act.dataset.hbTgAct === 'reserve') reserveSelected();
    else addSelected();
  }
}

function onCartUpdate(event) {
  const detail = event.detail || {};
  const source = (detail.data && detail.data.source) || detail.sourceId;
  if (source === 'hb-tgift') return;
  S.fails = 0;
  // HB 2026-10-10: the cart drawer's own requests (assets/hb-cart.js) bring this feature's state section along, so the
  // new cart is known the moment the drawer shows it; a change from elsewhere is fetched as before.
  const st = stateFrom(detail.data || {});
  if (st) {
    clearTimeout(S.refreshTimer);
    apply(st);
    return;
  }
  S.stale = true;
  scheduleRefresh(150);
}

function init() {
  document.addEventListener('click', onClick);
  document.addEventListener(EVENT, onCartUpdate);
  // A quick add tapped while the note is up: the note leaves at once, the quick add's own note has the floor.
  document.addEventListener('hb:qa-tap', () => hideNote());
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) scheduleRefresh(0);
  });

  if (dlg) {
    dlg.addEventListener('click', (e) => {
      const t = e.target instanceof Element ? e.target : null;
      if (!t) return;
      if (t.closest('[data-hb-tg-close]')) {
        e.preventDefault();
        closeSheet();
        return;
      }
      const sw = t.closest('.hb-tg-sw');
      if (sw) {
        e.preventDefault();
        if (!sw.disabled) sheetPick(sw.dataset.tgVid);
        return;
      }
      if (t.closest('[data-tg-primary]')) {
        e.preventDefault();
        if (!S.busy) sheetPrimary();
      }
    });
    dlg.addEventListener('cancel', (e) => {
      e.preventDefault();
      closeSheet();
    });
  }

  if (noteEl) {
    const hold = () => {
      S.noteHeld = true;
      clearTimeout(S.noteTimer);
    };
    const letGo = () => {
      S.noteHeld = false;
      armNote();
    };
    noteEl.addEventListener('mouseenter', hold);
    noteEl.addEventListener('mouseleave', letGo);
    noteEl.addEventListener('focusin', hold);
    noteEl.addEventListener('focusout', letGo);
    noteEl.querySelector('[data-tg-note-x]')?.addEventListener('click', () => hideNote());
    noteEl.querySelector('[data-tg-note-go]')?.addEventListener('click', () => {
      const kind = S.noteKind;
      hideNote();
      if (kind === 'incart') {
        const root = drawer();
        if (root && typeof root.open === 'function') root.open();
      } else {
        openSheet();
      }
    });
  }

  const root = drawer();
  if (root) {
    new MutationObserver(afterMorph).observe(root, { childList: true, subtree: true });
    let wasOpen = drawerOpen();
    new MutationObserver(() => {
      const open = drawerOpen();
      if (open === wasOpen) return;
      wasOpen = open;
      if (open) {
        hideNote();
        S.hadMod = !!mod();
        paintMod();
        setTimeout(arrive, 350); // after the drawer has slid in
      } else {
        S.cartClosedAt = Date.now();
        if (S.notePending) {
          // HB 2026-10-10 (Vic): she left the cart as the gift came within reach; the page's note makes the offer, 5 s on.
          const p = S.notePending;
          S.notePending = null;
          if (noteKindFor(S.cur) === p.kind && !seenKinds().includes(p.kind)) showNote(p.kind, p.vid);
        }
      }
    }).observe(root, { attributes: true, attributeFilter: ['open'], subtree: true });
  }

  S.hadMod = !!mod();
  paintAll();
  decide();

  // The note on a collection page: once per visit for each kind. The first one waits until she has been browsing
  // for 20 s (counted from her first collection page of the visit, so a quick move to another collection
  // does not restart the wait); a kind that follows a cart change comes after the usual 0.9 s.
  if (noteEl && DATA.notePage) {
    let first = Number(recall(FIRST_KEY)) || 0;
    if (!first) {
      first = Date.now();
      remember(FIRST_KEY, String(first));
    }
    const due = Math.max(NOTE_DELAY_MS, NOTE_VISIT_MS - (Date.now() - first));
    const kind = noteKindFor(S.cur);
    if (kind && !seenKinds().includes(kind)) showNote(kind, undefined, due);
    let lastKind = kind;
    document.addEventListener(EVENT, () => {
      setTimeout(() => {
        const k = noteKindFor(S.cur);
        if (k && k !== lastKind && !seenKinds().includes(k)) showNote(k);
        lastKind = k;
      }, 900);
    });
  }

  window.HBGift = { version: '2026-10-10d', state: S, refresh, open: openSheet };
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
